import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { ChevronRight, MapPin, Users } from 'lucide-react'
import type { StashCardPresentation } from '../../store/stash-scoped/types'

function formatDistance(meters: number): string {
  return `${(meters / 1609.34).toFixed(1)} mi`
}

interface StashCardListProps {
  cards: StashCardPresentation[]
  onCardClick?: (card: StashCardPresentation) => void
}

export function StashCardList({ cards, onCardClick }: StashCardListProps) {
  return (
    <div className="px-4 md:px-6 lg:px-8 mt-4 pb-24">
      <div className="bg-card rounded-xl shadow-sm overflow-hidden divide-y divide-border">
        {cards.map((card) => (
          <StashCardListItem key={card.id} card={card} {...(onCardClick && { onClick: onCardClick })} />
        ))}
      </div>
    </div>
  )
}

interface StashCardListItemProps {
  card: StashCardPresentation
  onClick?: (card: StashCardPresentation) => void
}

function StashCardListItem({ card, onClick }: StashCardListItemProps) {
  return (
    <button
      onClick={() => onClick?.(card)}
      className="w-full flex items-start gap-3 p-4 hover:bg-muted/50 transition-colors text-left group"
    >
      {/* Stasher avatar */}
      <Avatar className="h-9 w-9 mt-0.5 shrink-0">
        <AvatarImage src={card.stashedBy.avatarUrl ?? undefined} className="" />
        <AvatarFallback className="bg-[var(--stash-blue)]/10 text-[var(--stash-blue)] text-xs font-semibold">
          {card.stashedBy.name.charAt(0)}
        </AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Category breadcrumb */}
        <p className="text-[11px] font-medium text-muted-foreground mb-0.5">{card.categoryPath}</p>

        {/* Vendor name */}
        <h3 className="text-sm font-semibold leading-tight group-hover:text-[var(--stash-blue)] transition-colors">
          {card.displayName}
        </h3>

        {/* Stashed-by line + social proof */}
        <div className="flex items-center gap-1 mt-1">
          <span className="text-[11px] text-muted-foreground">by {card.stashedBy.name}</span>
          {card.stasherCount > 1 && (
            <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
              <Users className="w-3 h-3" />
              {card.stasherCount}
            </span>
          )}
        </div>

        {/* Note */}
        {card.note && (
          <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2 leading-relaxed">
            &ldquo;{card.note}&rdquo;
          </p>
        )}

        {/* Tags */}
        {card.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {card.tags.map((tag) => (
              <Badge
                key={tag}
                variant="secondary"
                className="text-[10px] font-medium px-1.5 py-0 h-5 rounded-full"
                render={<span />}
              >
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>

      {/* Right side: distance + chevron */}
      <div className="flex flex-col items-end gap-1 shrink-0 pt-1">
        {card.distanceMeters !== undefined && (
          <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
            <MapPin className="w-3 h-3" />
            {formatDistance(card.distanceMeters)}
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors mt-1" />
      </div>
    </button>
  )
}
